import axios, { AxiosPromise, AxiosResponse } from "axios";
import RestApi from "../../utils/RestApi";
import { Authenticator } from ".";
import {
    BehaviorSubject,
    interval,
    Observable,
    ObservableLike,
    PartialObserver,
    Subscribable,
    Unsubscribable
    } from "rxjs";
import { Capability } from "./Capability";
import { List } from "immutable";
import { sample } from "rxjs/operators";

export interface ICapabilities
{
    has(capability: Capability | undefined): boolean;
}

class Capabilities implements ICapabilities
{
    constructor(private readonly _capabilities: { [endpoint: string]: List<string> } = {}) { }

    public has(capability: Capability): boolean
    {
        if (!capability)
        {
            return true;
        }

        const caps = this._capabilities[capability.endpoint];
        if (!caps)
        {
            return false;
        }

        if (caps.contains(capability.name))
        {
            return true;
        }

        return false;
    }

    public with(endpoint: string, capabilities: string[]): Capabilities
    {
        const next = {};
        next[endpoint] = List.of(...capabilities);
        const result = new Capabilities({ ...this._capabilities, ...next });
        console.debug("adding capabilities for endpoint " + endpoint + " resulting in: " + JSON.stringify(result._capabilities));
        return result;
    }

    public isLoaded(endpoint: string)
    {
        return !!this._capabilities[endpoint];
    }

    public getEndpoints(): List<string>
    {
        return List.of(...Object.getOwnPropertyNames(this._capabilities));
    }
}

class CapabilityManagerImpl implements Subscribable<ICapabilities>
{
    private _subject = new BehaviorSubject<Capabilities>(new Capabilities());

    constructor()
    {
        Authenticator.subscribe(l =>
        {
            console.log("reloading");

            const capabilities = this._subject.getValue().getEndpoints();
            for (const key of capabilities)
            {
                this.loadCapabilities(key);
            }
        });
    }

    public async has(capability: Capability | undefined): Promise<boolean>
    {
        if (!capability)
        {
            return true;
        }

        if (!this._subject.getValue().isLoaded(capability.endpoint))
        {
            try
            {
                await this.loadCapabilities(capability.endpoint);
            }
            catch (e)
            {
                console.error(`Error occured getting capabilities from ${capability.endpoint}: ${e}`);
                return false;
            }
        }

        const result = this._subject.getValue().has(capability);
        return result;
    }

    public subscribe(observerOrNext?: PartialObserver<ICapabilities> | ((value: ICapabilities) => void), error?: (error: any) => void, complete?: () => void): Unsubscribable
    {
        return this._subject
            .pipe(sample(interval(100)))
            .subscribe(...arguments);
    }

    private async loadCapabilities(endpoint: string): Promise<void>
    {
        console.debug("loading capabilities from " + endpoint);
        const url = await RestApi.getUrl(endpoint);

        const response = await axios.get(url);

        let capabilities: string[];
        if (Array.isArray(response.data))
        {
            capabilities = response.data;
        }
        else if (typeof (response.data) === "object")
        {
            capabilities = [];
            for (const key in response.data)
            {
                if (response.data.hasOwnProperty(key))
                {
                    const element = response.data[key];
                    capabilities.push(`${element}`);
                }
            }
        }
        else
        {
            capabilities = [response.data];
        }

        this._subject.next(this._subject.getValue().with(endpoint, capabilities));
    }
}

// tslint:disable-next-line:variable-name
export let CapabilityManager = new CapabilityManagerImpl();
